import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../core/providers/AuthProvider';
import MainMenu from '../../components/MainMenu';
import Avatar from '../../../core/design-system/components/Avatar';
import { Icon } from '../../../core/design-system/components/Icon';
import { Logo } from '../../../core/design-system/components/Logo';
import { useTheme } from '../../../core/providers/ThemeProvider';
import { useMediaQuery } from '../../../shared/hooks/useMediaQuery';
import { useUnreadNotificationsCount } from '../../../features/notifications/hooks/useNotifications';
import { ROUTES } from '../../../core/constants/routes';
import OnboardingModal from '../../../features/onboarding/components/OnboardingModal';
import ScrollToTop from '../../../shared/components/Scroll/ScrollToTop';
import ToastProvider from '../../../core/providers/ToastProvider';
import CreateActivityAssistant from '../../../features/posts/components/CreateActivityAssistant';
import './styles.css';

const DashboardLayout = () => {
  const { t } = useTranslation('common');
  const { currentTheme } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { user, logout } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Mapping des routes vers les titres (vide sur home LLM - pattern Perplexity)
  const getPageTitle = () => {
    const path = location.pathname;
    
    // Home conversationnelle : pas de titre (branding suffit)
    if (path.includes('/dashboard/llm') || path === '/dashboard') {
      return '';
    }
    if (path.includes('/dashboard/feed')) {
      return t('menu.feed');
    }
    if (path.includes('/dashboard/my-activities')) {
      return t('menu.my_activities');
    }

    if (path.includes('/dashboard/notifications')) {
      return t('menu.notifications');
    }
    if (path.includes('/dashboard/profile')) {
      return t('menu.profile');
    }
    if (path.includes('/dashboard/settings')) {
      return t('pages.settings', 'Paramètres');
    }
    if (path.includes('/dashboard/activity/')) {
      return t('activity.default_title');
    }
    
    return '';
  };

  useEffect(() => {
    if (user && !user.onboardingCompleted) {
      setShowOnboarding(true);
    }
  }, [user]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [isMobile]);

  // Fermer drawer/modal avec Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showLogoutConfirm) {
          setShowLogoutConfirm(false);
        } else if (showCreatePost) {
          setShowCreatePost(false);
        } else if (drawerOpen) {
          setDrawerOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen, showLogoutConfirm, showCreatePost]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const unreadCount = useUnreadNotificationsCount();

  const handleNotificationsClick = () => {
    window.scrollTo(0, 0);
    navigate(`${ROUTES.DASHBOARD}/notifications`);
  };

  const handleUserHeaderClick = () => {
    closeDrawer();
    navigate(`${ROUTES.DASHBOARD}/profile`);
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      setShowLogoutConfirm(false);
      closeDrawer();
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const handleCreatePostClick = () => {
    setShowCreatePost(true);
  };

  const handleCloseCreatePost = () => {
    setShowCreatePost(false);
  };

  const handlePostCreated = () => {
    setShowCreatePost(false);
    // Rafraîchir si on est sur le feed
    if (location.pathname.includes('/dashboard/feed')) {
      window.location.reload();
    }
  };

  const pageTitle = getPageTitle();

  // Déterminer le type de page pour adapter la navbar (memoizé pour éviter re-calculs)
  const pageType = useMemo(() => {
    const path = location.pathname;
    
    // Page d'accueil LLM (Rask conversationnel)
    if (path.includes('/dashboard/llm') || path === '/dashboard') {
      return 'rask_home';
    }
    // Fiche activité
    if (path.includes('/dashboard/activity/')) {
      return 'detail_page';
    }
    // Profil d'un autre utilisateur (avec :uid dans l'URL)
    // /dashboard/profile/:uid vs /dashboard/profile (mon profil)
    const profileMatch = path.match(/^\/dashboard\/profile\/(.+)$/);
    if (profileMatch) {
      return 'detail_page';
    }
    // Autres pages principales
    return 'main_page';
  }, [location.pathname]);

  // Navigation vers Rask (pour le bouton robot)
  const handleRaskClick = () => {
    window.scrollTo(0, 0);
    navigate(ROUTES.DASHBOARD_LLM);
  };

  // Navigation retour (avec fallback vers dashboard si pas d'historique - deep link depuis email)
  const handleBackClick = () => {
    // Vérifier si on peut vraiment faire un "back" (historique existe dans l'app)
    // history.length <= 2 signifie qu'on est arrivé directement sur cette page (deep link)
    if (window.history.length <= 2) {
      // Pas d'historique précédent, retourner au dashboard
      navigate(ROUTES.DASHBOARD_LLM);
    } else {
      window.history.back();
    }
  };

  // Initiales pour l'avatar fallback
  const getUserInitials = () => {
    if (user?.displayName) {
      return user.displayName.charAt(0).toUpperCase();
    }
    return '?';
  };

  return (
    <ToastProvider>
      <div className={`dashboard-container ${currentTheme === 'dark' ? 'dark' : ''}`}>
        <ScrollToTop />
        
        {/* Top bar : branding + titre + actions */}
        <div className="dashboard-topbar">
          {/* Left: burger OU bouton retour (pages détail: activité, profil autre user) + branding */}
          <div className="topbar-left">
            {pageType === 'detail_page' ? (
              <button 
                className="topbar-back-button"
                onClick={handleBackClick}
                aria-label={t('actions.back', 'Retour')}
                title={t('actions.back', 'Retour')}
              >
                <Icon name="arrow-left" size={24} />
              </button>
            ) : (
              <button 
                className="burger-button"
                onClick={toggleDrawer}
                aria-label="Toggle menu"
              >
                <Icon name="list" size={24} />
              </button>
            )}
            <div className="topbar-brand">
              <Logo size={24} />
              <span className="topbar-brand-name">Raskmote</span>
            </div>
          </div>

          {/* Center: titre de page (vide sur home) */}
          {pageTitle && (
            <h1 className="topbar-title">{pageTitle}</h1>
          )}

          {/* Right: actions - adaptées selon le type de page */}
          <div className="topbar-right">
            {/* Fiche activité : aucune action à droite (retour est à gauche) */}

            {/* Accueil Rask : notif + bouton créer */}
            {pageType === 'rask_home' && (
              <>
                {/* Bouton créer une activité */}
                <button 
                  className="notifications-button"
                  onClick={handleCreatePostClick}
                  aria-label={t('topbar.create_activity', 'Créer une activité')}
                  title={t('topbar.create_activity', 'Créer une activité')}
                >
                  <Icon name="plus" size={24} />
                </button>

                {/* Icône notifications */}
                <button 
                  className={`notifications-button ${unreadCount > 0 ? 'has-unread' : ''}`}
                  onClick={handleNotificationsClick}
                  aria-label={unreadCount > 0 
                    ? t('topbar.notifications_unread', 'Notifications ({{count}} non lues)', { count: unreadCount })
                    : t('topbar.notifications', 'Notifications')
                  }
                >
                  <Icon 
                    name="bell" 
                    size={24}
                    variant={unreadCount > 0 ? 'filled' : 'outline'}
                  />
                  {unreadCount > 0 && (
                    <span className="notifications-dot"></span>
                  )}
                </button>
              </>
            )}

            {/* Autres pages principales : bouton robot vers Rask */}
            {pageType === 'main_page' && (
              <button 
                className="topbar-rask-button"
                onClick={handleRaskClick}
                aria-label={t('topbar.rask_assistant', 'RASK Assistant')}
                title={t('topbar.rask_assistant', 'RASK Assistant')}
              >
                <Icon name="robot" size={24} />
              </button>
            )}
          </div>
        </div>

        {/* Overlay pour fermeture drawer */}
        {drawerOpen && (
          <div 
            className="drawer-overlay" 
            onClick={closeDrawer}
            aria-hidden="true"
          />
        )}

        <div className="dashboard-layout">
          {/* Drawer (ouverture via burger) */}
          <nav className={`drawer-menu ${drawerOpen ? 'drawer-open' : 'drawer-closed'}`}>
            {/* Header utilisateur */}
            <div className="drawer-user-header" onClick={handleUserHeaderClick}>
              <Avatar 
                src={user?.photoURL} 
                text={getUserInitials()} 
                size="md"
                className="drawer-user-avatar"
              />
              <div className="drawer-user-info">
                <span className="drawer-user-name">
                  {user?.displayName || t('menu.profile')}
                </span>
                <span className="drawer-user-subtitle">
                  {t('drawer.view_profile', 'Voir le profil')}
                </span>
              </div>
              <Icon name="chevron-right" size={20} className="drawer-user-chevron" />
            </div>

            {/* Navigation - zone scrollable */}
            <div className="drawer-nav-container">
              <MainMenu onNavigate={closeDrawer} />
            </div>

            {/* Footer avec Logout */}
            <div className="drawer-footer">
              <button className="drawer-logout-button" onClick={handleLogoutClick}>
                <Icon name="logout" size={20} />
                <span>{t('drawer.logout', 'Déconnexion')}</span>
              </button>
            </div>
          </nav>

          {/* Contenu principal */}
          <main className="content-container">
            <div className="dashboard-content">
              <Outlet />
            </div>
          </main>
        </div>
        
        {showOnboarding && (
          <OnboardingModal onComplete={handleOnboardingComplete} />
        )}

        {/* Modal de confirmation logout */}
        {showLogoutConfirm && (
          <div className="logout-modal-overlay" onClick={handleLogoutCancel}>
            <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="logout-modal-title">
                {t('drawer.logout_confirm_title', 'Se déconnecter ?')}
              </h3>
              <p className="logout-modal-message">
                {t('drawer.logout_confirm_message', 'Êtes-vous sûr de vouloir vous déconnecter ?')}
              </p>
              <div className="logout-modal-actions">
                <button 
                  className="logout-modal-cancel" 
                  onClick={handleLogoutCancel}
                >
                  {t('actions.cancel', 'Annuler')}
                </button>
                <button 
                  className="logout-modal-confirm" 
                  onClick={handleLogoutConfirm}
                >
                  {t('drawer.logout', 'Déconnexion')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal création de post */}
        {showCreatePost && (
          <CreateActivityAssistant
            onClose={handleCloseCreatePost}
            onSuccess={handlePostCreated}
          />
        )}
      </div>
    </ToastProvider>
  );
};

export default DashboardLayout;
